import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { formsAPI } from '../services/api';
import PageHero from '../components/Common/PageHero';
import MetricCard from '../components/Common/MetricCard';
import DataGrid from '../components/Common/DataGrid';

type WorkspaceTab = 'overview' | 'activity' | 'audit' | 'communications';

const FormWorkspace: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [noteBody, setNoteBody] = useState('');
  const [activeTab, setActiveTab] = useState<WorkspaceTab>('overview');
  const [eventDateFrom, setEventDateFrom] = useState('');
  const [eventDateTo, setEventDateTo] = useState('');
  const [actionDateFrom, setActionDateFrom] = useState('');
  const [actionDateTo, setActionDateTo] = useState('');
  const [auditDateFrom, setAuditDateFrom] = useState('');
  const [auditDateTo, setAuditDateTo] = useState('');
  const [auditSearch, setAuditSearch] = useState('');
  const [auditFieldFilter, setAuditFieldFilter] = useState('all');
  const [auditActorFilter, setAuditActorFilter] = useState('all');

  const { data, isLoading, isError, refetch } = useQuery(
    ['form-workspace', id],
    () => formsAPI.getWorkspace(id || ''),
    { enabled: Boolean(id) }
  );

  const addNoteMutation = useMutation(
    (payload: { body: string; kind?: string }) => formsAPI.addWorkspaceNote(id || '', payload),
    {
      onSuccess: () => {
        setNoteBody('');
        toast.success('Workspace note saved');
        queryClient.invalidateQueries(['form-workspace', id]);
      },
      onError: () => {
        toast.error('Failed to save workspace note');
      },
    }
  );

  if (isLoading) {
    return <div className="flex h-64 items-center justify-center text-gray-500">Loading workspace...</div>;
  }

  if (isError || !data) {
    return (
      <div className="rounded-3xl border border-red-100 bg-white p-8 shadow-sm">
        <h1 className="text-2xl font-black text-gray-900">Workspace unavailable</h1>
        <p className="mt-2 text-gray-600">The service-line workspace could not be loaded.</p>
        <button onClick={() => refetch()} className="mt-4 rounded-2xl bg-slate-900 px-5 py-3 font-semibold text-white">
          Retry
        </button>
      </div>
    );
  }

  const { form, auditLogs, usage100Record, relatedForms, notifications, operationalState, notes, recentEvents, allowedActions, actionSummary, emailHistory } = data;

  const isWithinDateRange = (rawDate: string | undefined, from: string, to: string) => {
    if (!rawDate) {
      return false;
    }

    const value = new Date(rawDate);
    if (Number.isNaN(value.getTime())) {
      return false;
    }

    if (from) {
      const fromDate = new Date(`${from}T00:00:00`);
      if (value < fromDate) {
        return false;
      }
    }

    if (to) {
      const toDate = new Date(`${to}T23:59:59.999`);
      if (value > toDate) {
        return false;
      }
    }

    return true;
  };

  const filteredRecentEvents = (recentEvents || []).filter((event: any) =>
    isWithinDateRange(event.timestamp, eventDateFrom, eventDateTo)
  );

  const normalizedActionSummary = (actionSummary || []).map((item: any) => ({
    timestamp: item.timestamp || item.createdAt,
    field: item.fieldName || item.title || item.type,
    oldValue: item.oldValue || '',
    newValue: item.newValue || item.body || '',
  }));

  const filteredActionSummary = normalizedActionSummary.filter((item: any) =>
    isWithinDateRange(item.timestamp, actionDateFrom, actionDateTo)
  );

  const auditFieldOptions = Array.from<string>(
    new Set((auditLogs || []).map((log: any) => log.fieldName).filter(Boolean))
  ).sort((left, right) => left.localeCompare(right));

  const auditActorOptions = Array.from<string>(
    new Set(
      (auditLogs || [])
        .map((log: any) => (log.user ? `${log.user.username} (${log.user.email})` : 'System'))
        .filter(Boolean)
    )
  ).sort((left, right) => left.localeCompare(right));

  const normalizedAuditSearch = auditSearch.trim().toLowerCase();
  const filteredAuditLogs = (auditLogs || []).filter((log: any) => {
    const actorLabel = log.user ? `${log.user.username} (${log.user.email})` : 'System';
    const matchesDate = isWithinDateRange(log.timestamp, auditDateFrom, auditDateTo);
    const matchesField = auditFieldFilter === 'all' || log.fieldName === auditFieldFilter;
    const matchesActor = auditActorFilter === 'all' || actorLabel === auditActorFilter;
    const matchesSearch =
      normalizedAuditSearch.length === 0 ||
      [
        log.fieldName,
        actorLabel,
        log.oldValue,
        log.newValue,
        log.timestamp,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(normalizedAuditSearch));

    return matchesDate && matchesField && matchesActor && matchesSearch;
  });

  const tabs: { id: WorkspaceTab; label: string; hint: string; count?: number }[] = [
    { id: 'overview', label: 'Overview', hint: 'State, ownership, notes', count: notes?.length || 0 },
    { id: 'activity', label: 'Activity', hint: 'Events and action summary', count: recentEvents?.length || 0 },
    { id: 'audit', label: 'Audit', hint: 'Field-level history', count: auditLogs?.length || 0 },
    { id: 'communications', label: 'Communications', hint: 'Notifications and email', count: (notifications?.length || 0) + (emailHistory?.length || 0) },
  ];

  const renderDateToolbar = (
    from: string,
    to: string,
    setFrom: React.Dispatch<React.SetStateAction<string>>,
    setTo: React.Dispatch<React.SetStateAction<string>>
  ) => (
    <div className="flex flex-wrap items-end gap-2">
      <label className="flex min-w-[8.25rem] flex-col gap-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
        From
        <input
          type="date"
          value={from}
          onChange={(event) => setFrom(event.target.value)}
          className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-medium normal-case tracking-normal text-slate-700 outline-none focus:border-blue-400"
        />
      </label>
      <label className="flex min-w-[8.25rem] flex-col gap-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
        To
        <input
          type="date"
          value={to}
          onChange={(event) => setTo(event.target.value)}
          className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-medium normal-case tracking-normal text-slate-700 outline-none focus:border-blue-400"
        />
      </label>
      <button
        onClick={() => {
          setFrom('');
          setTo('');
        }}
        className="rounded-xl border border-blue-900 bg-blue-900 px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-800"
      >
        Clear
      </button>
    </div>
  );

  const renderAuditToolbar = () => (
    <div className="flex flex-wrap items-end gap-2">
      <label className="flex min-w-[18rem] flex-[1.6] flex-col gap-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
        Search
        <div className="relative">
          <input
            type="text"
            value={auditSearch}
            onChange={(event) => setAuditSearch(event.target.value)}
            placeholder="Search actor, field, before, after..."
            className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 pl-10 text-sm font-medium normal-case tracking-normal text-slate-700 outline-none focus:border-blue-400"
          />
          <i className="fas fa-search pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400"></i>
        </div>
      </label>
      <label className="flex min-w-[10rem] flex-col gap-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
        Field
        <select
          value={auditFieldFilter}
          onChange={(event) => setAuditFieldFilter(event.target.value)}
          className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-medium normal-case tracking-normal text-slate-700 outline-none focus:border-blue-400"
        >
          <option value="all">All fields</option>
          {auditFieldOptions.map((field) => (
            <option key={field} value={field}>
              {field}
            </option>
          ))}
        </select>
      </label>
      <label className="flex min-w-[12rem] flex-col gap-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
        Actor
        <select
          value={auditActorFilter}
          onChange={(event) => setAuditActorFilter(event.target.value)}
          className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-medium normal-case tracking-normal text-slate-700 outline-none focus:border-blue-400"
        >
          <option value="all">All actors</option>
          {auditActorOptions.map((actor) => (
            <option key={actor} value={actor}>
              {actor}
            </option>
          ))}
        </select>
      </label>
      <label className="flex min-w-[8.25rem] flex-col gap-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
        From
        <input
          type="date"
          value={auditDateFrom}
          onChange={(event) => setAuditDateFrom(event.target.value)}
          className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-medium normal-case tracking-normal text-slate-700 outline-none focus:border-blue-400"
        />
      </label>
      <label className="flex min-w-[8.25rem] flex-col gap-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
        To
        <input
          type="date"
          value={auditDateTo}
          onChange={(event) => setAuditDateTo(event.target.value)}
          className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-medium normal-case tracking-normal text-slate-700 outline-none focus:border-blue-400"
        />
      </label>
      <button
        onClick={() => {
          setAuditSearch('');
          setAuditFieldFilter('all');
          setAuditActorFilter('all');
          setAuditDateFrom('');
          setAuditDateTo('');
        }}
        className="rounded-xl border border-blue-900 bg-blue-900 px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-800"
      >
        Clear
      </button>
    </div>
  );

  return (
    <div className="space-y-8">
      <PageHero
        badge={{ label: form.isActive ? 'Active service line' : 'Inactive service line', tone: form.isActive ? 'green' : 'red' }}
        title={form.customerName}
        description="Customer and service-line 360 workspace for operations, role-aware actions, history, and usage-driven follow-through."
        stats={[
          { icon: 'fas fa-satellite', label: `Service line: ${form.serviceLine}` },
          { icon: 'fas fa-id-card', label: `Customer ID: ${form.customerId || 'Unassigned'}` },
          { icon: 'fas fa-user', label: `Owner: ${form.owner?.username || form.submittedByEmail || 'Unknown'}` },
        ]}
        actions={
          <>
            <button onClick={() => navigate('/forms')} className="rounded-2xl bg-white/10 px-5 py-3 font-semibold text-white hover:bg-white/15">
              Back to forms
            </button>
            <button onClick={() => refetch()} className="rounded-2xl bg-white px-5 py-3 font-semibold text-slate-900 hover:bg-slate-100">
              Refresh workspace
            </button>
          </>
        }
      />

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-4">
        <MetricCard label="Current usage" value={`${Number(form.currentUsage).toFixed(1)} GB`} hint={`of ${Number(operationalState.totalAllowanceGb).toFixed(1)} GB total allowance`} icon="fas fa-chart-area" accent="blue" />
        <MetricCard label="Usage percentage" value={`${Number(form.usagePercentage || 0).toFixed(1)}%`} hint={operationalState.isAtCap ? 'At or above cap' : operationalState.isNearCap ? 'Near cap' : 'Within expected range'} icon="fas fa-gauge-high" accent={operationalState.isAtCap ? 'red' : operationalState.isNearCap ? 'amber' : 'green'} />
        <MetricCard label="Workspace alerts" value={notifications.length} hint={usage100Record ? '100% usage record exists' : 'No 100% record yet'} icon="fas fa-bell" accent="violet" />
        <MetricCard label="Allowed actions" value={allowedActions.isReadOnly ? 'Read only' : 'Operator'} hint={operationalState.assignment?.accountManager || 'No account manager'} icon="fas fa-user-shield" accent="cyan" />
      </div>

      <div className="rounded-3xl border border-gray-100 bg-white p-3 shadow-sm">
        <div className="flex flex-wrap gap-3">
          {tabs.map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`min-w-[11rem] flex-1 rounded-2xl border px-4 py-3 text-left transition-all ${
                  isActive
                    ? 'border-slate-900 bg-gradient-to-r from-slate-900 via-blue-900 to-indigo-900 text-white shadow-lg'
                    : 'border-gray-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50 hover:shadow-md'
                }`}
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="text-sm font-bold">{tab.label}</span>
                  {typeof tab.count === 'number' ? (
                    <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${isActive ? 'bg-white/15 text-white' : 'bg-slate-100 text-slate-700'}`}>
                      {tab.count}
                    </span>
                  ) : null}
                </div>
                <p className={`mt-1 text-xs ${isActive ? 'text-white/75' : 'text-slate-500'}`}>{tab.hint}</p>
              </button>
            );
          })}
        </div>
      </div>

      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-5">
          <div className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm xl:col-span-2">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-black text-gray-900">Operational overview</h2>
              <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
                {form.isCapped ? 'Capped package' : 'Uncapped package'}
              </span>
            </div>
            <div className="mt-5 grid grid-cols-1 gap-3">
              <div className="rounded-2xl bg-slate-50 p-3.5">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Package and topup</p>
                <p className="mt-1.5 text-base font-bold text-slate-900">{form.packageLimit} GB base</p>
                <p className="text-sm text-slate-600">Topup: {Number(form.dataTopup || 0).toFixed(1)} GB</p>
              </div>
              <div className="rounded-2xl bg-slate-50 p-3.5">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Ownership</p>
                <p className="mt-1.5 text-base font-bold text-slate-900">{operationalState.assignment?.accountManager || 'No account manager'}</p>
                <p className="text-sm text-slate-600">Owner: {operationalState.assignment?.owner?.email || operationalState.assignment?.submittedByEmail || 'Unknown'}</p>
              </div>
              <div className="rounded-2xl bg-slate-50 p-3.5">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Activation</p>
                <p className="mt-1.5 text-base font-bold text-slate-900">{form.activationDate ? new Date(form.activationDate).toLocaleDateString() : 'Unknown'}</p>
                <p className="text-sm text-slate-600">Status: {form.status}</p>
              </div>
              <div className="rounded-2xl bg-slate-50 p-3.5">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Role-aware actions</p>
                <p className="mt-1.5 text-base font-bold text-slate-900">{allowedActions.canAdjustPackage ? 'Admin actions enabled' : allowedActions.isReadOnly ? 'Read only' : 'Standard operator actions'}</p>
                <p className="text-sm text-slate-600">
                  {allowedActions.canTransferData ? 'Transfer/package workflows available' : 'No operational edits allowed'}
                </p>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm xl:col-span-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h2 className="text-xl font-black text-gray-900">Workspace notes</h2>
                <p className="mt-1 text-sm text-slate-500">Capture handoffs and updates without stretching the whole workspace.</p>
              </div>
              <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
                {notes?.length || 0} notes
              </span>
            </div>

            <div className="mt-5 grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1.1fr)_minmax(0,1.6fr)]">
              {allowedActions.canEditNotes && (
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-700">
                    <i className="fas fa-pen-to-square text-slate-500"></i>
                    Add note
                  </div>
                  <textarea
                    value={noteBody}
                    onChange={(event) => setNoteBody(event.target.value)}
                    rows={6}
                    placeholder="Capture a handoff, customer update, or operational note..."
                    className="w-full resize-none rounded-xl border border-gray-200 bg-white px-3 py-3 text-sm outline-none focus:border-blue-400"
                  />
                  <div className="mt-3 flex items-center justify-between gap-3">
                    <p className="text-xs text-slate-500">Keep it short and operational so handoffs stay scannable.</p>
                    <button
                      onClick={() => addNoteMutation.mutate({ body: noteBody })}
                      disabled={!noteBody.trim() || addNoteMutation.isLoading}
                      className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {addNoteMutation.isLoading ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                </div>
              )}

              <div className="rounded-2xl border border-gray-100 bg-white">
                <div className="border-b border-gray-100 px-4 py-3">
                  <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                    <i className="fas fa-notes-medical text-slate-500"></i>
                    Recent notes
                  </div>
                </div>
                <div className="grid max-h-[28rem] grid-cols-1 gap-3 overflow-y-auto p-4 sm:grid-cols-2">
                  {notes?.length === 0 ? (
                    <p className="text-sm text-gray-500">No workspace notes yet.</p>
                  ) : (
                    notes?.map((note: any) => (
                      <div key={note.id} className="rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 to-yellow-100 p-3 shadow-sm">
                        <div className="flex items-start justify-between gap-3">
                          <p className="text-sm font-semibold text-slate-900">{note.author?.username || 'Unknown author'}</p>
                          <span className="rounded-full bg-white/70 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-700">
                            {note.kind}
                          </span>
                        </div>
                        <p className="mt-2 line-clamp-4 text-sm leading-5 text-slate-700">{note.body}</p>
                        <p className="mt-3 text-[11px] font-medium text-slate-500">{new Date(note.createdAt).toLocaleString()}</p>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="xl:col-span-5">
            <DataGrid
              title="Related customer records"
              rows={relatedForms || []}
              emptyText="No related forms found for this customer ID."
              columns={[
                { key: 'serviceLine', header: 'Service line', render: (relatedForm: any) => <button onClick={() => navigate(`/forms/${relatedForm.id}/workspace`)} className="font-semibold text-blue-700 hover:text-blue-900">{relatedForm.serviceLine}</button> },
                { key: 'status', header: 'Status', render: (relatedForm: any) => relatedForm.isActive ? 'Active' : 'Inactive' },
                { key: 'usage', header: 'Usage', render: (relatedForm: any) => `${Number(relatedForm.currentUsage).toFixed(1)} GB / ${Number(relatedForm.packageLimit + (relatedForm.dataTopup || 0)).toFixed(1)} GB` },
              ]}
            />
          </div>
        </div>
      )}

      {activeTab === 'activity' && (
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
          <DataGrid
            title="Recent events"
            description="Notifications, notes, and operational audit events in one timeline."
            rows={filteredRecentEvents}
            emptyText="No recent events in the selected date range."
            toolbar={renderDateToolbar(eventDateFrom, eventDateTo, setEventDateFrom, setEventDateTo)}
            columns={[
              { key: 'timestamp', header: 'Timestamp', render: (event: any) => new Date(event.timestamp).toLocaleString() },
              { key: 'kind', header: 'Type', render: (event: any) => <span className="uppercase text-xs font-semibold text-gray-500">{event.kind}</span> },
              {
                key: 'details',
                header: 'Details',
                render: (event: any) => (
                  <div>
                    <div className="font-semibold text-gray-900">{event.title}</div>
                    <div className="text-sm text-gray-600">{event.description}</div>
                  </div>
                ),
              },
            ]}
          />

          <DataGrid
            title="Recent action summary"
            description="Package, topup, status, and email-related changes tied to this service line."
            rows={filteredActionSummary}
            emptyText="No operational actions in the selected date range."
            toolbar={renderDateToolbar(actionDateFrom, actionDateTo, setActionDateFrom, setActionDateTo)}
            columns={[
              { key: 'timestamp', header: 'Timestamp', render: (row: any) => row.timestamp ? new Date(row.timestamp).toLocaleString() : 'Unknown' },
              { key: 'field', header: 'Field', render: (row: any) => <span className="font-semibold text-gray-900">{row.field}</span> },
              { key: 'oldValue', header: 'Before', render: (row: any) => row.oldValue || 'Empty' },
              { key: 'newValue', header: 'After', render: (row: any) => row.newValue || 'Empty' },
            ]}
          />
        </div>
      )}

      {activeTab === 'audit' && (
        <DataGrid
          title="Audit timeline"
          rows={filteredAuditLogs}
          emptyText="No audit entries match the current filters."
          toolbar={renderAuditToolbar()}
          columns={[
            { key: 'timestamp', header: 'Timestamp', render: (log: any) => new Date(log.timestamp).toLocaleString() },
            { key: 'field', header: 'Field', render: (log: any) => <span className="font-semibold text-gray-900">{log.fieldName}</span> },
            { key: 'user', header: 'Actor', render: (log: any) => log.user ? `${log.user.username} (${log.user.email})` : 'System' },
            { key: 'change', header: 'Change', render: (log: any) => `${log.oldValue || 'Empty'} -> ${log.newValue || 'Empty'}` },
          ]}
        />
      )}

      {activeTab === 'communications' && (
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
          <DataGrid
            title="Notification history"
            rows={notifications || []}
            emptyText="No workspace notifications yet."
            columns={[
              { key: 'createdAt', header: 'When', render: (notification: any) => new Date(notification.createdAt).toLocaleString() },
              { key: 'title', header: 'Title', render: (notification: any) => <span className="font-semibold text-gray-900">{notification.title}</span> },
              { key: 'body', header: 'Details', render: (notification: any) => notification.body },
            ]}
          />

          <DataGrid
            title="Email history"
            rows={emailHistory || []}
            emptyText="No email-related events captured yet."
            columns={[
              { key: 'createdAt', header: 'When', render: (event: any) => new Date(event.createdAt).toLocaleString() },
              { key: 'source', header: 'Source', render: (event: any) => event.source || event.type || 'system' },
              { key: 'title', header: 'Details', render: (event: any) => <div><div className="font-semibold text-gray-900">{event.title}</div><div className="text-sm text-gray-600">{event.body}</div></div> },
            ]}
          />
        </div>
      )}
    </div>
  );
};

export default FormWorkspace;
